// SELF — your active deities as ONE scene in the temple hall (template screen
// 3): the five faculties for this Step stand free in the hall — no frames —
// each lit from behind by a gold radiance, the one the Step needs dominant.
// Seats already secured stand small and faint on the terraces behind them
// (part of the structure); seats not yet reached are silhouettes higher up,
// in cloud. Tap a figure to talk to it. The Step bar returns to NOW.
import React from 'react';
import { Image, Pressable, ScrollView, StyleSheet, Text, View, useWindowDimensions } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import Svg, { Defs, RadialGradient, Stop, Ellipse } from 'react-native-svg';
import type { GearId } from '../olmps/ascension';
import type { SelfState, SeatView } from './useSelf';
import type { NowStep } from './useCurrentStep';
import { GEAR_LABEL, cutoutFor } from './roster';
import { GlassCard, Kicker } from './ui';
import { DISPLAY_FONT, SECTOR_COLOR, SECTOR_NAME, makeStyles, useV10 } from './theme';

// Pentagon of the five in play (cx = centre x, base = feet line, h = figure
// height), all as fractions of the stage. Back row first so the front row
// overlaps it, like a group standing in depth.
const SLOTS: { gear: GearId; cx: number; base: number; h: number }[] = [
  { gear: 'orient', cx: 0.5, base: 0.52, h: 0.4 },
  { gear: 'revise', cx: 0.18, base: 0.7, h: 0.38 },
  { gear: 'regulate', cx: 0.82, base: 0.7, h: 0.38 },
  { gear: 'direct', cx: 0.72, base: 0.97, h: 0.46 },
  { gear: 'execute', cx: 0.29, base: 0.97, h: 0.46 },
];
const FIG_ASPECT = 0.72; // cutout width / height (3:4 portraits trimmed)

export function SelfView({
  self,
  step,
  onOpenDeity,
  onFocusStep,
}: {
  self: SelfState;
  step: NowStep | null;
  onOpenDeity: (seatId: string) => void;
  onFocusStep: () => void;
}) {
  const t = useV10();
  const styles = useStyles();
  const { width } = useWindowDimensions();
  const W = Math.min(width, 520);
  const H = W * 1.42;
  const current = step?.gear ?? null;
  const secured = self.all.filter((v) => v.state === 'secured');
  const veiled = self.all.filter((v) => v.state === 'veiled');

  return (
    <ScrollView contentContainerStyle={styles.scroll} showsVerticalScrollIndicator={false}>
      <View style={styles.head}>
        <Text style={styles.title}>Your active deities</Text>
        <Kicker color={t.ivorySoft}>Five faculties for this step</Kicker>
      </View>

      <View style={[styles.stage, { width: W, height: H }]}>
        {/* Higher terraces, in cloud: seats not yet reached, as silhouettes. */}
        {veiled.length ? (
          <View style={[styles.cloudBand, { top: H * 0.02, height: H * 0.2 }]} pointerEvents="none">
            <LinearGradient colors={['rgba(243,234,215,0)', 'rgba(243,234,215,0.10)', 'rgba(243,234,215,0)']} style={StyleSheet.absoluteFill} />
            {veiled.slice(0, 10).map((v, i, arr) => {
              const n = arr.length;
              const x = (0.08 + (0.84 * (i + 0.5)) / n) * W;
              const h = H * 0.11;
              const src = cutoutFor(v.seat.id);
              return src ? (
                <Image
                  key={v.seat.id}
                  source={src}
                  style={{ position: 'absolute', left: x - (h * FIG_ASPECT) / 2, bottom: 0, width: h * FIG_ASPECT, height: h, tintColor: 'rgba(243,234,215,0.11)' }}
                  resizeMode="contain"
                />
              ) : null;
            })}
          </View>
        ) : null}

        {/* Terraces behind: already-secured seats, small, part of the structure. */}
        {secured.length ? (
          <View style={[styles.terrace, { top: H * 0.2, height: H * 0.22 }]} pointerEvents="none">
            {secured.slice(0, 10).map((v, i, arr) => {
              const n = arr.length;
              const x = (0.1 + (0.8 * (i + 0.5)) / n) * W;
              const h = H * 0.2;
              const src = cutoutFor(v.seat.id);
              return src ? (
                <Image key={v.seat.id} source={src} style={{ position: 'absolute', left: x - (h * FIG_ASPECT) / 2, bottom: 0, width: h * FIG_ASPECT, height: h, opacity: 0.45 }} resizeMode="contain" />
              ) : null;
            })}
            <LinearGradient colors={['rgba(7,11,23,0)', 'rgba(7,11,23,0.75)']} style={[StyleSheet.absoluteFill, { top: '55%' }]} />
          </View>
        ) : null}

        {/* The five in play. */}
        {SLOTS.map((slot) => {
          const v = self.byGear[slot.gear];
          if (!v) return null;
          const lit = slot.gear === current;
          const h = slot.h * H * (lit ? 1.08 : 1);
          const w = h * FIG_ASPECT;
          return (
            <Figure
              key={slot.gear}
              v={v}
              lit={lit}
              w={w}
              h={h}
              style={{ position: 'absolute', left: slot.cx * W - w / 2, top: slot.base * H - h }}
              onPress={() => onOpenDeity(v.seat.id)}
            />
          );
        })}
        {/* Labels above every figure (own layer so the front row never hides them). */}
        {SLOTS.map((slot) => {
          const v = self.byGear[slot.gear];
          if (!v) return null;
          const lit = slot.gear === current;
          const held = !v.stats.secured;
          const labelW = W * 0.42;
          return (
            <View key={`l-${slot.gear}`} pointerEvents="none" style={[styles.figText, { left: Math.max(0, Math.min(W - labelW, slot.cx * W - labelW / 2)), width: labelW, top: slot.base * H - H * 0.075, zIndex: 5 }]}>
              <Text style={[styles.figGear, lit && { color: t.goldBright, fontSize: 19 }]}>{GEAR_LABEL[v.seat.gear].name.toUpperCase()}</Text>
              <Text style={styles.figTag}>{GEAR_LABEL[v.seat.gear].tag}</Text>
              <Text style={[styles.figName, held && { color: t.maladyText }]} numberOfLines={1}>
                {held ? `${v.seat.name} · ${v.maladyName || 'malady'} holds` : v.seat.name}
              </Text>
            </View>
          );
        })}
        {/* Floor mist so feet sit in the hall. */}
        <LinearGradient pointerEvents="none" colors={['rgba(7,11,23,0)', 'rgba(7,11,23,0.85)']} style={[StyleSheet.absoluteFill, { top: H * 0.86 }]} />
      </View>

      {step ? (
        <Pressable onPress={onFocusStep} style={{ alignSelf: 'stretch', marginTop: -8 }}>
          <GlassCard style={styles.stepBar}>
            <View style={[styles.stepIcon, { borderColor: step.sector ? SECTOR_COLOR[step.sector] : t.gold }]}>
              <Text style={[styles.stepIconText, { color: step.sector ? SECTOR_COLOR[step.sector] : t.gold }]}>
                {step.sector ? SECTOR_NAME[step.sector].slice(0, 1) : '●'}
              </Text>
            </View>
            <View style={{ flex: 1, gap: 2 }}>
              <Kicker>Current step</Kicker>
              <Text style={styles.stepTitle} numberOfLines={1}>
                {step.title}
              </Text>
              <Text style={styles.stepWhy} numberOfLines={1}>
                {step.why}
              </Text>
            </View>
            <View style={styles.chev}>
              <Text style={styles.chevText}>›</Text>
            </View>
          </GlassCard>
        </Pressable>
      ) : null}

      <Text style={styles.foot}>
        {self.rank} · {self.securedCount}/15 seats secured · tap a deity to talk
      </Text>
    </ScrollView>
  );
}

function Figure({ v, lit, w, h, style, onPress }: { v: SeatView; lit: boolean; w: number; h: number; style: object; onPress: () => void }) {
  const t = useV10();
  const styles = useStyles();
  const src = cutoutFor(v.seat.id);
  const glowW = w * (lit ? 1.9 : 1.4);
  return (
    <Pressable onPress={onPress} style={[style, { width: w, height: h, zIndex: lit ? 3 : 1 }]}>
      {/* Radiance behind the figure (brighter, wider for the lit one). */}
      <Svg pointerEvents="none" width={glowW} height={glowW * 1.1} style={{ position: 'absolute', left: w / 2 - glowW / 2, top: h * 0.3 - (glowW * 1.1) / 2 }}>
        <Defs>
          <RadialGradient id={`g-${v.seat.id}`} cx="50%" cy="50%" r="50%">
            <Stop offset="0" stopColor="#f0cf85" stopOpacity={lit ? 0.75 : 0.35} />
            <Stop offset="0.55" stopColor="#d6b46a" stopOpacity={lit ? 0.28 : 0.12} />
            <Stop offset="1" stopColor="#d6b46a" stopOpacity={0} />
          </RadialGradient>
        </Defs>
        <Ellipse cx={glowW / 2} cy={(glowW * 1.1) / 2} rx={glowW / 2} ry={(glowW * 1.1) / 2} fill={`url(#g-${v.seat.id})`} />
      </Svg>
      {src ? <Image source={src} style={{ width: w, height: h }} resizeMode="contain" /> : null}
      {/* Feet fade into the mist. */}
      <LinearGradient pointerEvents="none" colors={['rgba(7,11,23,0)', 'rgba(7,11,23,0.9)']} style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: h * 0.22 }} />
    </Pressable>
  );
}

const useStyles = makeStyles((t) =>
  StyleSheet.create({
    scroll: { paddingHorizontal: 0, paddingTop: 6, paddingBottom: 36, gap: 10, alignItems: 'center' },
    head: { alignItems: 'center', gap: 4, marginBottom: 2 },
    title: { fontFamily: DISPLAY_FONT, color: '#f3ead7', fontSize: 20, letterSpacing: 3, textTransform: 'uppercase' },
    stage: { alignSelf: 'center', overflow: 'visible' },
    cloudBand: { position: 'absolute', left: 0, right: 0 },
    terrace: { position: 'absolute', left: 0, right: 0 },
    figText: { position: 'absolute', alignItems: 'center', gap: 1 },
    figGear: { fontFamily: DISPLAY_FONT, color: '#f3ead7', fontSize: 16, letterSpacing: 1.8, textShadowColor: 'rgba(0,0,0,0.9)', textShadowRadius: 8 },
    figTag: { color: 'rgba(243,234,215,0.85)', fontSize: 11.5, fontWeight: '600', textShadowColor: 'rgba(0,0,0,0.9)', textShadowRadius: 6 },
    figName: { color: 'rgba(240,207,133,0.95)', fontSize: 9, fontWeight: '800', letterSpacing: 1, marginTop: 2, textShadowColor: 'rgba(0,0,0,0.9)', textShadowRadius: 6 },
    stepBar: { flexDirection: 'row', alignItems: 'center', gap: 12, padding: 14, marginHorizontal: 12 },
    stepIcon: { width: 44, height: 44, borderRadius: 22, borderWidth: 1.5, alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(7,11,23,0.5)' },
    stepIconText: { fontSize: 16, fontWeight: '900' },
    stepTitle: { color: t.ivory, fontSize: 17, fontWeight: '800' },
    stepWhy: { color: t.ivorySoft, fontSize: 12.5 },
    chev: { width: 34, height: 34, borderRadius: 17, borderWidth: 1, borderColor: t.gold, alignItems: 'center', justifyContent: 'center' },
    chevText: { color: t.goldBright, fontSize: 20, fontWeight: '700', marginTop: -2 },
    foot: { color: t.ivoryFaint, fontSize: 11.5, textAlign: 'center' },
  }),
);
